import type { IListRow } from '../../api/lists-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import type { IMyListTask } from '../../api/work-item-contracts';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { RowActions, WRITE_REASON } from './row-actions';

/**
 * A metrics update on My tasks (SR-315): the row's three presses — a request recorded, figures
 * entered, figures confirmed unchanged — at the survey's round, read here since a task names no
 * edition. Figures are entered for every series of the survey's catalogue, the edition's threshold
 * being Metrics updates' own choice.
 */
export function MetricsTask({
  task,
  row,
  onDone
}: {
  task: IMyListTask;
  row: IListRow;
  onDone: (sentence: string) => void;
}) {
  const can = useCan();
  const round = useAdminData((signal) => metricsUpdatesApi.round(task.survey, signal), [task.survey]);
  if (round.error) return <LoadError message={round.error} />;
  const updates = round.data;
  if (!updates) return null;
  if (!updates.round) {
    return (
      <p className="text-sm text-muted-foreground">
        No cycle pinning {task.surveyName} has an as-of date, so no round of figures is collected now.
      </p>
    );
  }
  return (
    <div className="grid gap-2" data-metrics-task>
      <p className="text-sm">
        {task.displayName}’s figures are owed as of <strong>{formatDay(updates.round.asOf)}</strong>{' '}
        (cycle {updates.round.cycleCode}).
      </p>
      {!can('internal_fields.write') && <NotPermitted id={WRITE_REASON} what="recording requests and figures" />}
      <RowActions
        row={row}
        name={task.displayName}
        survey={task.survey}
        asOf={updates.round.asOf}
        series={updates.figureSeries}
        thresholdSeries={[]}
        onDone={onDone}
      />
    </div>
  );
}
